import { Component, inject, Input, OnInit, ViewChild } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { BaseModalComponent } from 'components/modal/base-modal.component';
import { FileUploadComponent } from 'components/file-upload/file-upload.component';
import { ApiService } from 'services/api.service';
import { formatDatetime } from 'util/date.utility';
import type { InformationRequestDocumentItem } from 'types/information-request.interface';

@Component({
  selector: 'app-ir-process-history-result',
  standalone: true,
  imports: [TranslateModule, FileUploadComponent],
  templateUrl: './ir-process-history-result.component.html',
  styleUrl: './ir-process-history-result.component.scss'
})
export class IrProcessHistoryResultComponent extends BaseModalComponent implements OnInit {
  @Input() entry: any;
  @ViewChild('content') content: any;

  entryJson?: string;
  private apiService = inject(ApiService);

  ngOnInit(): void {
    const oldObj = this.tryParse(this.entry?.oldValues);
    const newObj = this.tryParse(this.entry?.newValues);
    this.entryJson = JSON.stringify({ oldValues: oldObj ?? this.entry?.oldValues, newValues: newObj ?? this.entry?.newValues }, null, 4);
  }

  show() {
    this.open(this.content);
  }

  downloadInitialPdf(): void {
    const requestId = this.entry?.informationRequestId;
    if (!requestId) return;
    this.apiService.getIrDocuments(requestId).subscribe({
      next: (documents: InformationRequestDocumentItem[]) => {
        const initialDoc = documents.find((d) => d.objectType === 'InformationRequestInitialPdfDocument');
        const initialFile = initialDoc?.files[0];
        if (!initialFile) return;
        this.apiService.downloadFile(initialFile.outerId).subscribe({
          next: (blob) => {
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = initialFile.uploadFileName;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
          }
        });
      }
    });
  }

  // Rows of a grouped history entry (LNA-1969); each renders through this component recursively.
  get groupRows(): any[] | null {
    return this.entry?.groupRows?.length > 1 ? this.entry.groupRows : null;
  }

  get type(): string {
    return this.entry?.type ?? '';
  }

  get oldValues(): string | null | undefined {
    return this.entry?.oldValues;
  }

  get newValues(): string | null | undefined {
    return this.entry?.newValues;
  }

  get isFileType(): boolean {
    // These must match InformationRequestChangeType enum values (r.type), not file ObjectType values.
    return [
      'CommunicationBackofficeFile',
      'CommunicationFrontofficeFile',
      'Document',
      'NoteFile',
      'OtherResponseFile',
      'ResponseDraftFile',
      'NamejsResponseFile',
      'CalculationFile',
      'InvoiceFile',
      'SendingEventFile'
    ].includes(this.type);
  }

  get isFileDeleted(): boolean {
    return this.isFileType && !this.newValues;
  }

  get deletedFileName(): string {
    return this.tryParse(this.oldValues)?.UploadFileName ?? '';
  }

  // LNA-1919: DocumentDescriptionEdited (type 36). newValues = { objectType, fileName, oldDescription, newDescription }
  get isDescriptionEdit(): boolean {
    return this.type === 'DocumentDescriptionEdited';
  }

  getDescriptionEditText(): string {
    const parsed = this.tryParse(this.newValues);
    const oldDescription = (parsed?.oldDescription ?? '').trim();
    const newDescription = (parsed?.newDescription ?? '').trim();
    // oldDescription null/empty = a first-time description was added
    if (oldDescription) return `"${oldDescription}" → "${newDescription}"`;
    return `"${newDescription}"`;
  }

  get fileOuterId(): string {
    return this.tryParse(this.newValues)?.OuterId ?? '';
  }

  get fileObjectType(): string {
    return this.tryParse(this.newValues)?.ObjectType ?? '';
  }

  get fileExisting(): any[] {
    const parsed = this.tryParse(this.newValues);
    return parsed ? [this.keysToLowercase(parsed)] : [];
  }

  // The file's text description (FileNote.Note) captured on the add/delete row (LNA-1919).
  get fileNoteText(): string {
    const parsed = this.tryParse(this.isFileDeleted ? this.oldValues : this.newValues);
    return parsed?.FileNote?.Note ?? '';
  }

  getCommunicationText(): string {
    return this.stripPrefix(this.newValues);
  }

  getPaymentTypesText(): string {
    const labels = ['Priek\u0161apmaksa', 'P\u0113capmaksa', 'Bezmaksas'];
    const format = (val: string | null | undefined): string[] | null => {
      if (!val) return null;
      const parts = val.split('|');
      return labels.map((_, i) => {
        const v = (parts[i] ?? '').trim();
        if (v === 'True') return 'Jā';
        if (v === 'False') return 'Nē';
        return '-';
      });
    };

    const oldFlags = format(this.oldValues);
    const newFlags = format(this.newValues);

    if (!newFlags) return '';

    const joinAll = (vals: string[]): string => labels.map((label, i) => `${label}: ${vals[i]}`).join(', ');
    if (!oldFlags) return joinAll(newFlags);

    // LNA-2010: list only the payment types whose value actually changed. The BE row carries all
    // three flags on both sides ("Pre|Post|NoCharge"), so the diff happens here - which also fixes
    // the rows already written to the history.
    const changed = labels
      .map((label, i) => ({ label, from: oldFlags[i], to: newFlags[i] }))
      .filter((flag) => flag.from !== flag.to);

    // Nothing differs (legacy rows written before the flags/note split) - keep both sides.
    if (!changed.length) return `${joinAll(oldFlags)} \u2192 ${joinAll(newFlags)}`;

    return changed.map((flag) => `${flag.label}: ${flag.from} \u2192 ${flag.to}`).join(', ');
  }

  getPaymentDateText(): string {
    const raw = this.newValues ?? '';
    const cleaned = raw.replace(/^"|"$/g, '');
    if (/^\d{4}-\d{2}-\d{2}/.test(cleaned)) return formatDatetime(cleaned);
    return raw;
  }

  getProcessingNotesText(): string {
    const oldText = this.stripPrefix(this.oldValues?.trim());
    const newText = this.stripPrefix(this.newValues?.trim());

    if (oldText && newText) {
      if (oldText === newText) return '';
      return `"${oldText}" main\u012Bta uz: "${newText}"`;
    }
    if (!oldText && newText) return `Pievienota: "${newText}"`;
    if (oldText && !newText) return `Dz\u0113sta: "${oldText}"`;
    return '';
  }

  getGenericText(): string {
    const oldRaw = this.oldValues;
    const newRaw = this.newValues;

    if (!oldRaw && !newRaw) return '';

    const oldObj = this.tryParse(oldRaw);
    const newObj = this.tryParse(newRaw);

    const oldIsObject = oldObj != null && typeof oldObj === 'object';
    const newIsObject = newObj != null && typeof newObj === 'object';

    if (oldIsObject || newIsObject) {
      const result = this.formatJsonChange(oldObj, newObj);
      if (result) return result;
      const oldSummary = this.summarizeJson(oldObj);
      const newSummary = this.summarizeJson(newObj);
      if (oldSummary && newSummary) return `"${oldSummary}" \u2192 "${newSummary}"`;
      if (newSummary) return `"${newSummary}"`;
      if (oldSummary) return `"${oldSummary}" no\u0146emts`;
      return '';
    }

    const oldV = this.formatIfDate(this.translateLabels(this.stripPrefix(oldRaw)));
    const newV = this.formatIfDate(this.translateLabels(this.stripPrefix(newRaw)));

    if (oldV && newV) return `"${oldV}" \u2192 "${newV}"`;
    if (newV) return `"${newV}"`;
    if (oldV) return `"${oldV}" no\u0146emts`;
    return '';
  }

  get hasGenericContent(): boolean {
    return !!this.getGenericText();
  }

  private formatJsonChange(oldObj: any, newObj: any): string {
    const oldFlat = this.flattenObject(oldObj);
    const newFlat = this.flattenObject(newObj);

    const allKeys = new Set([
      ...Object.keys(oldFlat),
      ...Object.keys(newFlat)
    ]);

    const changes: string[] = [];
    for (const key of allKeys) {
      const oldStr = this.formatValue(oldFlat[key] ?? '');
      const newStr = this.formatValue(newFlat[key] ?? '');

      if (oldStr === newStr) continue;

      if (oldStr && newStr) {
        changes.push(`"${oldStr}" \u2192 "${newStr}"`);
      } else if (newStr) {
        changes.push(`"${newStr}"`);
      } else if (oldStr) {
        changes.push(`"${oldStr}" no\u0146emts`);
      }
    }

    return changes.join(', ') || '';
  }

  private formatIfDate(val: string): string {
    const stripped = val.replace(/^"|"$/g, '');
    if (/^\d{4}-\d{2}-\d{2}/.test(stripped)) return formatDatetime(stripped);
    return val;
  }

  private formatValue(val: string): string {
    if (!val) return '';
    if (this.TERM_MAP[val]) return this.TERM_MAP[val];
    const stripped = val.replace(/^"|"$/g, '');
    if (/^\d{4}-\d{2}-\d{2}/.test(stripped)) return formatDatetime(stripped);
    return val;
  }

  private flattenObject(obj: any, prefix = ''): Record<string, string> {
    if (!obj || typeof obj !== 'object') return {};

    if (Array.isArray(obj)) {
      const items = obj.map((item) => {
        if (item && typeof item === 'object') {
          const vals = Object.values(this.flattenObject(item));
          return vals.join(', ');
        }
        return String(item ?? '');
      }).filter(Boolean);
      if (items.length) {
        return { [prefix || '_arr']: items.join('; ') };
      }
      return {};
    }

    const result: Record<string, string> = {};
    for (const key of Object.keys(obj)) {
      const fullKey = prefix ? `${prefix}.${key}` : key;
      let val = obj[key];

      if (typeof val === 'string') {
        const parsed = this.tryParse(val);
        if (parsed && typeof parsed === 'object') {
          val = parsed;
        }
      }

      if (val != null && typeof val === 'object') {
        Object.assign(result, this.flattenObject(val, fullKey));
      } else {
        const str = val == null || val === '' ? '' : String(val);
        if (str) result[fullKey] = str;
      }
    }
    return result;
  }

  private summarizeJson(obj: any): string {
    if (!obj) return '';
    const flat = this.flattenObject(obj);
    const values = Object.values(flat).filter(Boolean);
    return values.join(', ');
  }

  private readonly TERM_MAP: Record<string, string> = {
    // Response types
    Reply: 'Atbilde',
    ForwardingLetter: 'P\u0101rs\u016Bt\u012B\u0161anas v\u0113stule',
    OtherResponse: 'Cita atbilde',
    NamejsReply: 'Namejs atbilde',
    NamejsForwarding: 'Namejs pārsūtīšanas vēstule',
    // Delivery methods
    Email: 'E-pasts',
    EAddress: 'E-adrese',
    Archive: 'Arh\u012Bv\u0101',
    PostalMail: 'Pa pastu',
    Portal: 'Port\u0101ls',
    // Deadlines
    Days30: '30 dienas',
    Days15: '15 darba dienas',
    Days10: '10 darba dienas',
    Other: 'Cits',
    Urgent: 'Steidzams',
    // Purpose
    PrivateUse: 'Priv\u0101t\u0101m vajadz\u012Bb\u0101m',
    SubmissionToInstitution: 'Iesnieg\u0161anai iest\u0101d\u0113 Latvij\u0101',
    SubmissionToForeignInstitution: 'Iesnieg\u0161anai iest\u0101d\u0113 \u0101rvalst\u012Bs',
    // Requester relation
    ForSelf: 'Par sevi',
    ForOtherPerson: 'Par citu personu',
    AuthorizationOrPermission: 'Ar pilnvarojumu vai rakstveida at\u013Cauju',
    DeceasedRelative: 'Par miru\u0161u radinieku',
    OtherCase: 'Cits gad\u012Bjums',
    // Payer
    Requester: 'Piepras\u012Bt\u0101js',
    OtherPhysicalPerson: 'Cita fiziska persona',
    OtherLegalEntity: 'Cita juridiska persona',
    FreeOfCharge: 'Bezmaksas',
    // Discount
    None: 'Nav',
    FirstOrSecondGroupDisability: 'Persona ar 1. vai 2. grupas invalidit\u0101ti',
    LowIncomePerson: 'Persona, kas atbilst tr\u016Bc\u012Bg\u0101s personas statusam',
    PoliticallyRepressed: 'Politiski repres\u0113t\u0101 persona',
    ChildProtection: 'B\u0113rna ties\u012Bbu aizsardz\u012Bba',
    Invoice: 'Rēķins',
    // Document types
    ArchiveCertificate: 'Arh\u012Bva izzi\u0146a',
    DocumentCopy: 'Dokumenta kopija',
    DocumentExtract: 'Dokumenta izraksts',
    DocumentTranscript: 'Dokumenta noraksts',
    DocumentOriginal: 'Dokumenta ori\u0123in\u0101ls',
    Genealogy: '\u0122enealo\u0123ija',
    CancelledRequest: 'Atcelts pieprasījums',
    // Task statuses
    New: 'Jauns',
    Assigned: 'Piešķirts',
    Accepted: 'Akceptēts',
    InProgress: 'Izpildē',
    Completed: 'Izpildīts',
    Cancelled: 'Atsaukts',
    // IR statuses
    Submitted: 'Iesniegts',
    PreliminaryResearch: 'Priekšizpēte',
    Processing: 'Apstrādē',
    // Boolean
    true: 'J\u0101',
    false: 'N\u0113',
    True: 'J\u0101',
    False: 'N\u0113'
  };

  private translateLabels(value: string): string {
    let result = value
      .replace(/\border (\d+)/gi, 'sec\u012Bba $1')
      .replace(/\bstatus /gi, 'statuss ')
      .replace(/^added\s*\|?\s*/i, 'Pievienots: ')
      .replace(/^updated\s*\|?\s*/i, 'Atjaunin\u0101ts: ')
      .replace(/^deleted\s*\|?\s*/i, 'Dz\u0113sts: ')
      .replace(/^removed\s*\|?\s*/i, 'No\u0146emts: ')
      .replace(/consolidated=True/g, 'konsolid\u0113ts: J\u0101')
      .replace(/consolidated=False/g, 'konsolid\u0113ts: N\u0113');

    for (const [en, lv] of Object.entries(this.TERM_MAP)) {
      result = result.replace(new RegExp(`\\b${en}\\b`, 'g'), lv);
    }
    return result;
  }

  private stripPrefix(value: string | null | undefined): string {
    if (!value) return '';
    const parts = value.split('|');
    if (parts.length < 2) return value;

    const first = parts[0].trim();
    if (/^[a-z][a-zA-Z]*$/.test(first)) {
      const messageParts = parts.slice(1).filter((p) => {
        const trimmed = p.trim();
        return trimmed && !/^[a-zA-Z_]+=[^\s]*$/.test(trimmed);
      });
      const message = messageParts.join('|').trim();
      if (message) return message;
    }
    return value;
  }

  private keysToLowercase(obj: any): any {
    if (Array.isArray(obj)) {
      return obj.map((item) => this.keysToLowercase(item));
    } else if (obj && typeof obj === 'object') {
      return Object.keys(obj).reduce((acc: any, key: string) => {
        const newKey = key.charAt(0).toLowerCase() + key.slice(1);
        acc[newKey] = this.keysToLowercase(obj[key]);
        return acc;
      }, {});
    }
    return obj;
  }

  private tryParse(value: string | null | undefined): any {
    if (!value) return null;
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }
}
